import { Box, makeStyles, Typography } from '@material-ui/core';
import React from 'react';
import { useModalContext } from '@components/Modal/ModalContextProvider';
import { BLOCK_RELEASE_REQUEST_CONFIRM_MODAL } from '@components/Modal/modalConstants';
import { BaseButton } from '@components/BaseButton/BaseButton';
import { useCreatePendingReleaseEvent } from '@data/blockReleases/hooks/useCreatePendingReleaseEvent';
import { PendingReleaseEventType } from '@data/blockReleases/types/PendingReleaseEvent.types';
import { getDateFromApiDateTime } from '@calendar';
import { formatInTimeZone } from 'date-fns-tz';
import { BlockReleaseConfirmModalProps } from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestConfirmModal/BlockReleaseRequestConfirmModal';
import { useFormContext } from 'react-hook-form';
import { PartialBlockReleaseRequestForm } from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestPage/BlockDetailsSection/PartialReleaseSection/PartialReleaseRequest.types';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';

type BlockReleaseConfirmModalButtonsProps = Omit<BlockReleaseConfirmModalProps, 'modalValues' | 'blockReleaseInfo'>;

const useStyles = makeStyles((theme) => ({
	buttonContainer: {
		display: 'flex',
		marginTop: theme.spacing(3),
		justifyContent: 'space-between',
		height: '48px',
	},
	inlineError: {
		marginRight: theme.spacing(3),
		fontWeight: 600,
		color: '#E00019',
	},
}));

export const BlockReleaseRequestConfirmModalButtons: React.FC<BlockReleaseConfirmModalButtonsProps> = ({
	facilityInfo,
	selectedRelease,
}) => {
	const classes = useStyles();
	const { createPendingReleaseEvent, inlineError } = useCreatePendingReleaseEvent();
	const { closeModal } = useModalContext();
	const { getValues } = useFormContext<PartialBlockReleaseRequestForm>();
	const { originalReleases, state } = selectedRelease;
	const isFlipRelease = originalReleases && originalReleases.length > 0 && state === BlockReleaseState.FLIP_RECOMMENDATION;
	const slotId = selectedRelease.slotId;
	const blockStart = selectedRelease.startDateTime;
	const startDate = blockStart && formatInTimeZone(getDateFromApiDateTime(blockStart), facilityInfo.hospitalTimeZone, 'yyyy-MM-dd');
	const shouldRenderButtons = slotId && startDate;

	if (!shouldRenderButtons) return null;

	const createReleaseEvent = (slotId: string, startTime: string, endTime: string) => {
		createPendingReleaseEvent(slotId, {
			event: PendingReleaseEventType.SSM_RELEASE,
			body: {
				date: startDate,
				startTime, // must be >= block start time
				endTime, // must be <= block end time
			},
		});
	};

	const handleCloseModal = () =>
		closeModal(BLOCK_RELEASE_REQUEST_CONFIRM_MODAL.id + slotId);

	const handleReleaseButtonClick = () => {
		if (isFlipRelease) {
			originalReleases.forEach((release) => {
				const originalReleaseSlotId = release.slotId;
				const startTime = formatInTimeZone(getDateFromApiDateTime(release.startDateTime), facilityInfo.hospitalTimeZone, 'HH:mm');
				const endTime = formatInTimeZone(getDateFromApiDateTime(release.endDateTime), facilityInfo.hospitalTimeZone, 'HH:mm');
				createReleaseEvent(originalReleaseSlotId, startTime, endTime);
			});
		} else {
			const { startTime, endTime } = getValues();
			createReleaseEvent(slotId, startTime, endTime);
		}
	};

	return (
		<Box className={classes.buttonContainer}>
			<BaseButton
				dataField="blockRelease-blockReleaseRequestModal-cancel"
				color="primary"
				variant="text"
				onClick={handleCloseModal}
			>
				Cancel
			</BaseButton>
			<Box display="flex" alignItems="center">
				{inlineError && <Typography className={classes.inlineError}>{inlineError}</Typography>}
				<BaseButton
					dataField="blockRelease-blockReleaseRequestModal-release"
					variant="contained"
					color="primary"
					onClick={handleReleaseButtonClick}
					disabled={!!inlineError}
				>
					Yes, release
				</BaseButton>
			</Box>
		</Box>
	);
};
